import type { IFaqResponse } from '@gik/api/acf/gik-settings/faq';
import { useFaq } from '@gik/api/acf/gik-settings/faq';
import { FetchProvider } from '@gik/core/components/DynamicComponent/FetchProvider';
import { InViewport } from '@gik/core/components/InViewport';
import type { WordpressFAQItem } from '@gik/core/models/wordpress/WordpressFAQItem';
import { ErrorPageSection } from '@gik/core/pages/Error/ErrorPageSection';
import type { UIComponent } from '@gik/core/types/UI';
import bemBlock from '@gik/core/utils/bemBlock';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import { CreateClickedLocation } from '@gik/create/enums/CreateAnalyticsEnums';
import { FAQStructuredData } from '@gik/seo/components/StructuredData';
import type { AccordionItemType } from '@gik/ui/Accordion';
import { Accordion } from '@gik/ui/Accordion';
import { PageHeader } from '@gik/ui/gik/PageHeader';
import { PageSection } from '@gik/ui/gik/PageSection';
import { StartInkindCard } from '@gik/ui/gik/StartInkindCard';
import { TestimonialsCarouselSection } from '@gik/ui/gik/TestimonialsCarouselSection';
import { HTMLParser } from '@gik/ui/HTMLParser';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { MarketingNavSection } from '../../components/MarketingNav/MarketingNavSection';
import { translationKeys } from './i18n/en';

function removeParagraph(title: string): string {
  title = title.trim();
  if (title.startsWith('<p>') && title.endsWith('</p>')) {
    title = title.substring('<p>'.length, title.length - '</p>'.length);
  }
  return title;
}

function createAccordionItems(faqItems: WordpressFAQItem[]): AccordionItemType[] {
  return faqItems?.map(item => ({
    title: HTMLParser({ rawHtml: removeParagraph(item.question) }),
    content: HTMLParser({ rawHtml: item.answer }),
  }));
}

export type CommonQuestionsPageServerProps = {
  faq: IFaqResponse;
};

export type CommonQuestionsPageProps = {
  fallbackData?: CommonQuestionsPageServerProps;
} & UIComponent;

export const CommonQuestionsPageBlockName = 'common-questions-page';

function CommonQuestionsPageComp(): React.ReactElement {
  const bem = bemBlock(CommonQuestionsPageBlockName);
  const { t } = useTranslation();

  return (
    <FAQStructuredData>
      <article className={bem()}>
        <InViewport height={160}>
          <PageHeader variant="sage" decoration="foliage">
            <h1>{t(translationKeys.title)}</h1>
          </PageHeader>
        </InViewport>

        <InViewport height={185}>
          <PageSection className={bem('lead')} noPadBottom>
            <p className="gik-section-lead">{t(translationKeys.leadDescription)}</p>
          </PageSection>
        </InViewport>

        <InViewport height={1634}>
          <FetchProvider<IFaqResponse, AccordionItemType[]>
            fetch={useFaq}
            transform={data => createAccordionItems(data?.faq)}
          >
            {({ transformed: FAQItems }) => (
              <PageSection>
                <Accordion items={FAQItems} headingLevel={2} dividers />
              </PageSection>
            )}
          </FetchProvider>
        </InViewport>

        <InViewport height={175}>
          <StartInkindCard trackingId={CreateClickedLocation.FAQ_PAGE} />
        </InViewport>

        <InViewport height={394}>
          <TestimonialsCarouselSection />
        </InViewport>

        <InViewport height={461}>
          <MarketingNavSection variant="sage" />
        </InViewport>
      </article>
    </FAQStructuredData>
  );
}

export const CommonQuestionsPage = withComponentErrorBoundary(CommonQuestionsPageComp, {
  ErrorFallback: ErrorPageSection,
});
